import { Card, CardContent, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { MemoryOutlined } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import { formatDuration } from './ops-format';
import type { ProcessorOpsStatisticsDto } from '@/types/dtos/ops/ops-platform.dto';

type ProcessorThroughputTableProps = {
  statistics: ProcessorOpsStatisticsDto;
};

export default function ProcessorThroughputTable({ statistics }: ProcessorThroughputTableProps) {
  const t = useTranslations();
  const { throughput, waitEstimates } = statistics;

  const rows = [
    {
      label: t('admin.opsAdmin.avgThroughput', undefined, 'Avg throughput (queued → ready)'),
      value: formatDuration(throughput.averageThroughputSeconds),
      note:
        throughput.p50ThroughputSeconds != null ? `p50 ${formatDuration(throughput.p50ThroughputSeconds)}` : undefined,
    },
    {
      label: t('admin.opsAdmin.avgProcessing', undefined, 'Avg processing (active)'),
      value: formatDuration(throughput.averageProcessingSeconds),
      note: undefined,
    },
    {
      label: t('admin.opsAdmin.expectedWaitNew', undefined, 'Expected wait (new job)'),
      value: formatDuration(waitEstimates.expectedWaitNewJobSeconds),
      note: t(
        'admin.opsAdmin.parallelism',
        { n: waitEstimates.parallelism },
        `parallelism ${waitEstimates.parallelism}`,
      ),
    },
  ];

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent sx={{ 'py': 1.25, 'px': 1.5, '&:last-child': { pb: 1.25 } }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
          <MemoryOutlined fontSize="small" color="action" />
          <Typography variant="subtitle2">
            {t('admin.opsAdmin.statisticsTitle', undefined, 'Processor performance')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ({throughput.windowHours}h, {throughput.sampleCount} samples)
          </Typography>
        </Stack>
        <Table size="small" sx={{ '& td, & th': { border: 0, py: 0.4, px: 0 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '45%' }}>
                <Typography variant="caption" color="text.secondary">
                  {t('admin.opsAdmin.throughputTable.metric', undefined, 'Metric')}
                </Typography>
              </TableCell>
              <TableCell sx={{ width: '20%' }}>
                <Typography variant="caption" color="text.secondary">
                  {t('admin.opsAdmin.throughputTable.value', undefined, 'Value')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  {t('admin.opsAdmin.throughputTable.note', undefined, 'Note')}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.label}>
                <TableCell>
                  <Typography variant="caption">{row.label}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {row.value}
                  </Typography>
                </TableCell>
                <TableCell>
                  {row.note && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                      {row.note}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
